import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/environment';
import { toast } from 'sonner';
import { formatDateFR, formatTimeFR } from '../utils/formatFR';
import { Mic, MicOff, Camera, CameraOff, PhoneOff, Circle, Clock, CheckCircle, Brain, FileText, Loader2, ChevronDown, ArrowRight, Video, Monitor, Shield, ShieldAlert, AlertTriangle, MessageCircle } from 'lucide-react';
import useWebSpeechPII from '../hooks/useWebSpeechPII';
import PiiWarningToast from '../components/PiiWarningToast';

// LiveKit imports (conditional — graceful fallback if not available)
let LiveKitRoom, VideoConference, lkUseTrackToggle, lkUseChat, lkUseRoomContext, LkTrack, LkDisconnectReason;
let lkUseTracks, LkRoomAudioRenderer, LkParticipantTile;
try {
  const lkComponents = require('@livekit/components-react');
  LiveKitRoom = lkComponents.LiveKitRoom;
  VideoConference = lkComponents.VideoConference;
  lkUseTrackToggle = lkComponents.useTrackToggle;
  lkUseChat = lkComponents.useChat;
  lkUseRoomContext = lkComponents.useRoomContext;
  lkUseTracks = lkComponents.useTracks;
  LkRoomAudioRenderer = lkComponents.RoomAudioRenderer;
  LkParticipantTile = lkComponents.ParticipantTile;
  const lkClient = require('livekit-client');
  LkTrack = lkClient.Track;
  LkDisconnectReason = lkClient.DisconnectReason;
  require('@livekit/components-styles');
} catch {
  // LiveKit not available — will use Jitsi fallback
}

const API = getApiUrl();

// ============================================================
// PII Detection — Blocage données personnelles temps réel
// ============================================================
const PII_PATTERNS = [
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/,           // Email
  /(?:0[1-9]|(?:\+33|0033)\s?[1-9])[\s.-]?(?:\d{2}[\s.-]?){4}/,     // FR phone (06, +33, 0033)
  /(?:\+\d{1,3}[\s.-]?)?\(?\d{2,4}\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}/, // International phone
  /https?:\/\/[^\s<]+/,                                               // URLs
  /www\.[^\s<]+/,                                                     // www links
];
const PII_WARNING = "Le partage de données personnelles est interdit. Merci de respecter les règles de la plateforme.";

function containsPII(text) {
  if (!text) return false;
  return PII_PATTERNS.some(p => p.test(text));
}

function sanitizeForReport(text) {
  if (!text) return text;
  let clean = text;
  PII_PATTERNS.forEach(p => { clean = clean.replace(p, '[DONNÉES MASQUÉES]'); });
  return clean;
}

// Report display component — handles both legacy and triple-output format
function ReportDisplay({ report, role }) {
  const globalReport = report.global_report || report;
  const coachFeedback = report.coach_feedback;
  const userFeedback = report.user_feedback;

  const ListSection = ({ title, items }) => items && items.length > 0 ? (
    <div className="mb-4">
      <h4 className="text-white text-xs font-semibold mb-2">{title}</h4>
      <ul className="space-y-1.5">{items.map((item, i) => (
        <li key={i} className="text-zinc-400 text-xs leading-relaxed flex gap-2">
          <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">•</span>
          <span>{typeof item === 'object' ? (
            <>
              <span className="text-zinc-300 font-medium">{item.nom || item.point || item.axe || item.action || ''}</span>
              {(item.description || item.impact || item.encouragement) && <> — {item.description || item.impact || item.encouragement}</>}
              {item.citation && <span className="block mt-1 text-[#D4AF37]/70 italic text-[11px]">"{item.citation}"</span>}
              {item.exercice && <span className="block mt-1 text-emerald-400/80 text-[11px]">Exercice : {item.exercice}</span>}
            </>
          ) : item}</span>
        </li>
      ))}</ul>
    </div>
  ) : null;

  const ScoreCard = ({ label, score }) => score ? (
    <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-xl p-4 text-center mb-4">
      <p className="text-2xl font-bold text-[#D4AF37]">{score.note}/10</p>
      <p className="text-zinc-400 text-xs mt-1">{label} — {score.justification}</p>
    </div>
  ) : null;

  const TextSection = ({ title, text }) => text ? (
    <div className="mb-4">
      <h4 className="text-white text-xs font-semibold mb-1">{title}</h4>
      <p className="text-zinc-400 text-xs leading-relaxed">{text}</p>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      {/* Global Report */}
      <div className="bg-[#141415] border border-white/10 rounded-2xl p-5">
        <h3 className="text-[#D4AF37] text-sm font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Rapport global
        </h3>
        <TextSection title="Résumé" text={globalReport.resume} />
        {globalReport.theme_analysis && (
          <div className="mb-4 bg-[#1a1a1b] border border-[#D4AF37]/20 rounded-xl p-3">
            <h4 className="text-white text-xs font-semibold mb-2 flex items-center gap-2">
              <span>🎯</span> Analyse thématique
            </h4>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Thème initial</p>
                <p className="text-zinc-300 text-xs">{globalReport.theme_analysis.theme_initial || '—'}</p>
              </div>
              <div>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Thème réellement traité</p>
                <p className="text-zinc-300 text-xs">{globalReport.theme_analysis.theme_reel || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                globalReport.theme_analysis.ecart_observe
                  ? 'bg-red-500/15 text-red-300 border border-red-500/30'
                  : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
              }`}>
                {globalReport.theme_analysis.ecart_observe ? 'Écart observé' : 'Cohérent'}
              </span>
            </div>
            {globalReport.theme_analysis.explication_evolution && (
              <p className="text-zinc-400 text-xs leading-relaxed">{globalReport.theme_analysis.explication_evolution}</p>
            )}
          </div>
        )}
        <ListSection title="Thématiques" items={globalReport.thematiques} />
        <ListSection title="Points clés" items={globalReport.points_cles || globalReport.moments_cles} />

        {/* Signaux faibles observés — différenciateur premium (nouveau pipeline 2-pass) */}
        {Array.isArray(globalReport.signaux_observes) && globalReport.signaux_observes.length > 0 && (
          <div className="mb-4">
            <h4 className="text-white text-xs font-semibold mb-2 uppercase tracking-wider">Signaux faibles observés</h4>
            <div className="space-y-2">
              {globalReport.signaux_observes.map((s, i) => (
                <div key={i} className="bg-[#1a1a1b] border-l-2 border-amber-500/40 rounded-r-lg p-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300">
                      {s.type?.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {s.citation && <p className="text-zinc-400 text-[11px] italic mb-1">« {s.citation} »</p>}
                  {s.lecture_pedagogique && <p className="text-zinc-300 text-xs leading-relaxed">{s.lecture_pedagogique}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        <ListSection title="Axes de travail" items={globalReport.axes_travail || globalReport.axes_amelioration} />
        <ListSection title="Prochaines étapes" items={globalReport.prochaines_etapes || (globalReport.prochaine_etape ? [globalReport.prochaine_etape] : [])} />
        <ScoreCard label="Progression" score={globalReport.score_progression} />
      </div>

      {/* Coach Feedback — PRIVATE, coach only */}
      {role === 'coach' && coachFeedback && (
        <div className="bg-[#141415] border border-blue-500/20 rounded-2xl p-5" data-testid="coach-private-feedback">
          <h3 className="text-blue-400 text-sm font-semibold mb-1 flex items-center gap-2">
            <Brain className="w-4 h-4" /> Votre feedback coach
          </h3>
          <p className="text-zinc-500 text-xs mb-4">Visible uniquement par vous — référentiel ICF</p>

          {/* Grille ICF (si disponible — nouveau format 2-pass) */}
          {coachFeedback.icf_competences && (
            <div className="mb-5">
              <h4 className="text-blue-300 text-xs font-semibold uppercase tracking-wider mb-3">8 compétences ICF</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { key: 'presence', label: 'Présence' },
                  { key: 'ecoute', label: 'Écoute active' },
                  { key: 'questionnement', label: 'Questionnement' },
                  { key: 'communication_directe', label: 'Communication directe' },
                  { key: 'prise_conscience', label: 'Prise de conscience' },
                  { key: 'plan_action', label: 'Plans d\'action' },
                  { key: 'responsabilisation', label: 'Responsabilisation' },
                  { key: 'gerer_progression', label: 'Gestion progression' },
                ].map(({ key, label }) => {
                  const c = coachFeedback.icf_competences[key];
                  if (!c) return null;
                  const note = c.note || 0;
                  const color = note >= 7 ? 'emerald' : note >= 5 ? 'amber' : 'red';
                  return (
                    <div key={key} className="bg-[#1a1a1b] border border-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-zinc-200 text-xs font-semibold">{label}</span>
                        <span className={`text-${color}-400 text-sm font-bold`}>{note}/10</span>
                      </div>
                      {c.observation && <p className="text-zinc-400 text-[11px] leading-relaxed mb-1.5">{c.observation}</p>}
                      {c.axe_progression && (
                        <p className="text-blue-300/80 text-[11px] leading-relaxed">
                          <span className="font-semibold">→ Axe :</span> {c.axe_progression}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Synthèses courtes (compat ancien format ou complément) */}
          {!coachFeedback.icf_competences && (
            <>
              <TextSection title="Qualité d'écoute" text={coachFeedback.ecoute} />
              <TextSection title="Questionnement" text={coachFeedback.questionnement} />
              <TextSection title="Structure" text={coachFeedback.structure} />
              <TextSection title="Relances" text={coachFeedback.relances} />
              <TextSection title="Capacité à faire progresser" text={coachFeedback.capacite_progression} />
            </>
          )}
          <ListSection title="Points forts" items={coachFeedback.points_forts} />
          <ListSection title="Axes d'amélioration" items={coachFeedback.axes_amelioration} />
          <ScoreCard label="Prestation coach" score={coachFeedback.score_prestation} />
        </div>
      )}

      {/* User Feedback — PRIVATE, user only */}
      {role === 'user' && userFeedback && (
        <div className="bg-[#141415] border border-emerald-500/20 rounded-2xl p-5" data-testid="user-private-feedback">
          <h3 className="text-emerald-400 text-sm font-semibold mb-1 flex items-center gap-2">
            <Brain className="w-4 h-4" /> Votre feedback personnel
          </h3>
          <p className="text-zinc-500 text-xs mb-4">Visible uniquement par vous</p>
          <TextSection title="Engagement" text={userFeedback.engagement} />
          <TextSection title="Clarté d'expression" text={userFeedback.clarte_expression} />
          <TextSection title="Réflexion" text={userFeedback.reflexion} />
          <TextSection title="Progression" text={userFeedback.progression} />
          <TextSection title="Posture" text={userFeedback.posture} />
          <ListSection title="Points forts" items={userFeedback.points_forts} />
          <ListSection title="Axes d'amélioration" items={userFeedback.axes_amelioration} />
          <ScoreCard label="Engagement" score={userFeedback.score_engagement} />
        </div>
      )}
    </div>
  );
}

// ============================================================
// LiveKit Error Boundary — catches SDK layout errors gracefully
// ============================================================
class LiveKitErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) {
    // Silently catch updatePages / useVisualStableUpdate errors
    if (error?.message?.includes('not part of the array') || error?.message?.includes('updatePages')) return;
    console.warn('[LiveKit] Caught error:', error?.message);
  }
  render() {
    if (this.state.hasError) {
      // Auto-recover after brief delay
      setTimeout(() => this.setState({ hasError: false }), 500);
      return <div className="flex-1 flex items-center justify-center bg-[#0A0A0B] text-zinc-500 text-sm">Récupération vidéo...</div>;
    }
    return this.props.children;
  }
}

// ============================================================
// LiveKitInner — Custom layout (NO VideoConference/updatePages)
// Uses useTracks + ParticipantTile = zero placeholder crashes
// ============================================================
function LiveKitInner({ chatOpen, setChatOpen, onEndCall, setPiiAlert }) {
  const micToggle = lkUseTrackToggle({ source: LkTrack.Source.Microphone });
  const camToggle = lkUseTrackToggle({ source: LkTrack.Source.Camera });
  const { send: chatSend, chatMessages } = lkUseChat();
  const room = lkUseRoomContext();

  // Get tracks WITHOUT placeholders — eliminates updatePages/useVisualStableUpdate crashes
  const tracks = lkUseTracks(
    [
      { source: LkTrack.Source.Camera, withPlaceholder: false },
      { source: LkTrack.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  const [screenSharePending, setScreenSharePending] = useState(false);
  const [screenShareActive, setScreenShareActive] = useState(false);

  // Detect screen share capability once
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const screenShareAvailable = typeof navigator?.mediaDevices?.getDisplayMedia === 'function';

  // Track screen share state from room events
  useEffect(() => {
    if (!room) return;
    const updateState = () => {
      setScreenShareActive(room.localParticipant?.isScreenShareEnabled || false);
    };
    updateState();
    room.on('localTrackPublished', updateState);
    room.on('localTrackUnpublished', updateState);
    return () => {
      room.off('localTrackPublished', updateState);
      room.off('localTrackUnpublished', updateState);
    };
  }, [room]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendChat = () => {
    const msg = chatInput.trim();
    if (!msg) return;
    if (containsPII(msg)) {
      setPiiAlert(true);
      setTimeout(() => setPiiAlert(false), 5000);
      setChatInput('');
      return;
    }
    chatSend(msg);
    setChatInput('');
  };

  // Custom screen share handler with full error handling
  const handleScreenShare = useCallback(async () => {
    if (screenSharePending) return;

    // Unsupported — informative message (not an error)
    if (!screenShareAvailable) {
      if (isMobile) {
        toast('Le partage d\'écran nécessite un ordinateur. Ouvrez cette session depuis Chrome, Firefox ou Edge sur PC/Mac.', { icon: '💻', duration: 5000 });
      } else {
        toast('Votre navigateur ne supporte pas le partage d\'écran. Utilisez Chrome, Firefox ou Edge.', { icon: '⚠️', duration: 4000 });
      }
      return;
    }

    setScreenSharePending(true);
    try {
      const isCurrentlySharing = room?.localParticipant?.isScreenShareEnabled;
      await room.localParticipant.setScreenShareEnabled(!isCurrentlySharing);
      setScreenShareActive(!isCurrentlySharing);
    } catch (err) {
      console.error('[ScreenShare] Error:', err);
      // Sync state regardless of error
      setScreenShareActive(room?.localParticipant?.isScreenShareEnabled || false);
      if (err.name === 'NotAllowedError') {
        toast.error('Permission de partage d\'écran refusée par le système');
      } else if (err.name === 'NotSupportedError' || err.name === 'TypeError') {
        toast('Cette fonctionnalité nécessite un navigateur de bureau (Chrome, Firefox, Edge).', { icon: '💻', duration: 5000 });
      } else if (err.name === 'AbortError') {
        // User cancelled the screen picker — silent
      } else if (err.name === 'InvalidAccessError') {
        // RTCPeerConnection sender conflict — gracefully ignore, track already removed
      } else {
        toast.error('Erreur partage d\'écran : ' + (err.message || 'inconnue'));
      }
    } finally {
      setScreenSharePending(false);
    }
  }, [room, screenSharePending, screenShareAvailable, isMobile]);

  return (
    <>
      {/* Audio renderer — separate from video layout */}
      <LkRoomAudioRenderer />

      {/* Video area — custom grid, NO VideoConference/updatePages */}
      <div className="flex-1 relative overflow-hidden" style={{ minHeight: 0 }}>
        <LiveKitErrorBoundary>
          <div className="h-full w-full" data-lk-theme="default">
            <div className={`h-full w-full grid gap-1 p-1 ${
              tracks.length <= 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'
            }`} style={{ gridAutoRows: '1fr' }}>
              {tracks.map((trackRef) => (
                <LkParticipantTile
                  key={trackRef.participant.sid + '-' + trackRef.source}
                  trackRef={trackRef}
                  className="rounded-lg overflow-hidden bg-[#1a1a1b]"
                />
              ))}
              {tracks.length === 0 && (
                <div className="flex flex-col items-center justify-center text-zinc-500 gap-3">
                  <Video className="w-10 h-10 opacity-30" />
                  <span className="text-sm">En attente du participant...</span>
                </div>
              )}
            </div>
          </div>
        </LiveKitErrorBoundary>
        <div className="absolute bottom-2 right-3 pointer-events-none z-10" data-testid="coachdia-branding">
          <span className="text-white text-xs font-semibold tracking-[0.25em] uppercase"
            style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.5)' }}>
            COACH DIA
          </span>
        </div>
      </div>

      {/* Chat drawer — overlay right side */}
      {chatOpen && (
        <div className="absolute right-0 top-[52px] z-40 w-72 sm:w-80 bg-[#141415]/95 backdrop-blur-sm border-l border-white/10 flex flex-col"
          style={{ bottom: 64 }} data-testid="chat-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 flex-shrink-0">
            <span className="text-white text-xs font-medium">Messages</span>
            <button onClick={() => setChatOpen(false)} className="text-zinc-400 hover:text-white text-xs px-2 py-1 bg-white/5 rounded">Fermer</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chatMessages.map((msg, i) => (
              <div key={msg.id || i} className={`flex flex-col ${msg.from?.isLocal ? 'items-end' : 'items-start'}`}>
                <span className="text-zinc-500 text-[10px] mb-0.5">{msg.from?.name || msg.from?.identity || 'Participant'}</span>
                <p className={`text-xs px-2.5 py-1.5 rounded-lg inline-block max-w-[85%] break-words ${
                  msg.from?.isLocal
                    ? 'bg-[#D4AF37]/20 text-white rounded-br-sm'
                    : 'bg-white/10 text-zinc-300 rounded-bl-sm'
                }`}>
                  {sanitizeForReport(msg.message)}
                </p>
              </div>
            ))}
            {chatMessages.length === 0 && (
              <p className="text-zinc-500 text-xs text-center mt-4">Aucun message pour le moment.</p>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="px-3 py-2 border-t border-white/10 flex gap-2 flex-shrink-0">
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Message..."
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs placeholder-zinc-500 outline-none focus:border-[#D4AF37]/50"
              data-testid="chat-input"
            />
            <button
              onClick={handleSendChat}
              className="px-3 py-2 bg-[#D4AF37] text-[#0A0A0B] rounded-lg text-xs font-medium hover:brightness-110 transition-all"
              data-testid="chat-send-btn"
            >
              Envoyer
            </button>
          </div>
        </div>
      )}

      {/* BOTTOM CONTROL BAR — hooked into LiveKit */}
      <div className="flex-shrink-0 bg-[#141415] border-t border-white/5 flex items-center justify-center gap-5 px-4 z-30"
        data-testid="fixed-control-bar"
        style={{ height: 64, paddingBottom: 'max(4px, env(safe-area-inset-bottom))' }}>
        <button
          {...micToggle.buttonProps}
          data-testid="btn-mic"
          className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${
            !micToggle.enabled ? 'bg-red-500/20 border-red-500/40 text-red-400' : 'bg-white/8 border-white/15 text-white hover:bg-white/15'
          }`}>
          {micToggle.enabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button
          {...camToggle.buttonProps}
          data-testid="btn-camera"
          className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${
            !camToggle.enabled ? 'bg-red-500/20 border-red-500/40 text-red-400' : 'bg-white/8 border-white/15 text-white hover:bg-white/15'
          }`}>
          {camToggle.enabled ? <Camera className="w-5 h-5" /> : <CameraOff className="w-5 h-5" />}
        </button>
        <button
          data-testid="btn-chat"
          onClick={() => setChatOpen(!chatOpen)}
          className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${
            chatOpen ? 'bg-[#D4AF37]/20 border-[#D4AF37]/40 text-[#D4AF37]' : 'bg-white/8 border-white/15 text-white hover:bg-white/15'
          }`}>
          <MessageCircle className="w-5 h-5" />
        </button>
        <button
          data-testid="btn-share"
          onClick={handleScreenShare}
          disabled={screenSharePending}
          className={`w-12 h-12 rounded-full border flex items-center justify-center transition-all ${
            screenSharePending ? 'opacity-50 cursor-wait' :
            screenShareActive ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' :
            !screenShareAvailable ? 'bg-white/5 border-white/10 text-zinc-600' :
            'bg-white/8 border-white/15 text-white hover:bg-white/15'
          }`}>
          <Monitor className="w-5 h-5" />
        </button>
        <button
          data-testid="btn-hangup"
          onClick={() => { if (window.confirm('Quitter la session ?')) onEndCall(); }}
          className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-400 hover:bg-red-500/30 transition-all">
          <PhoneOff className="w-5 h-5" />
        </button>
      </div>
    </>
  );
}

export default function VideoSession() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jitsiLoaded, setJitsiLoaded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [callEnded, setCallEnded] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState(null); // null, 'transcribing', 'analyzing', 'generating', 'done', 'error'
  const [report, setReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [securityBanner, setSecurityBanner] = useState(true);
  const [piiAlert, setPiiAlert] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // 🔒 PII Live Detection (zéro coût — Web Speech API)
  const [piiDetection, setPiiDetection] = useState(null);
  const piiActive = isRecording && !callEnded;
  const { supported: piiSupported, listening: piiListening } = useWebSpeechPII({
    bookingId,
    token,
    apiUrl: API,
    active: piiActive,
    onDetection: setPiiDetection,
  });

  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const hasAutoStarted = useRef(false);
  const egressIdRef = useRef(null);
  const lkTokenRef = useRef(null);   // Cached LiveKit token — NEVER changes after first fetch
  const lkUrlRef = useRef(null);     // Cached LiveKit URL
  const sessionFetchedRef = useRef(false); // Guard against re-fetching
  const sessionTimerRef = useRef(null);    // Session elapsed timer
  const [sessionElapsed, setSessionElapsed] = useState(0);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Start server-side recording via LiveKit Egress
  const startEgress = useCallback(async () => {
    if (!bookingId || !token || egressIdRef.current) return;
    try {
      const res = await fetch(`${API}/marketplace/sessions/${bookingId}/start-recording`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        egressIdRef.current = data.egress_id;
        if (data.egress_id) {
          setIsRecording(true);
          setRecordingTime(0);
          if (!recordingTimerRef.current) {
            recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
          }
        }
      }
    } catch (err) {
      console.warn('[LiveKit] Server-side recording not available, using client recording');
    }
  }, [bookingId, token]);

  // Fetch session details — ONCE only, cache LiveKit credentials
  useEffect(() => {
    if (sessionFetchedRef.current || !token || !bookingId) return;
    sessionFetchedRef.current = true;

    const fetchSession = async () => {
      try {
        const res = await fetch(`${API}/marketplace/sessions/${bookingId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Session non trouvée');
        const data = await res.json();

        // Cache LiveKit credentials in refs — stable for entire session lifecycle
        if (data.livekit_token && !lkTokenRef.current) {
          lkTokenRef.current = data.livekit_token;
          lkUrlRef.current = data.livekit_url;
        }

        setSession(data);
      } catch (err) {
        toast.error(err.message);
        sessionFetchedRef.current = false; // Allow retry on error
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [token, bookingId]);

  // 🎯 Iter383 — Presence tracking ping (fallback du webhook LiveKit).
  // Envoie un "joined" au mount de la session active + heartbeat toutes les 30s
  // + "left" au unmount. Source de vérité pour la garde "Preuve d'échange".
  useEffect(() => {
    if (!session || !token || !bookingId) return;
    if (session.video_access_blocked) return; // Pas de ping si visio bloquée
    if (callEnded) return; // Pas de ping après la fin

    const sendPing = async (event) => {
      try {
        await fetch(`${API}/marketplace/sessions/${bookingId}/presence/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ event }),
        });
      } catch {
        // Silencieux — le webhook serveur reste la source primaire de vérité.
      }
    };

    sendPing('joined');
    const interval = setInterval(() => sendPing('heartbeat'), 30000);
    const onUnload = () => {
      // Best-effort sur navigateur fermé (sendBeacon serait idéal mais nécessite
      // un endpoint non-auth ou un token query-param — on garde fetch ici).
      try {
        navigator.sendBeacon && navigator.sendBeacon(
          `${API}/marketplace/sessions/${bookingId}/presence/ping`,
          new Blob([JSON.stringify({ event: 'left' })], { type: 'application/json' }),
        );
      } catch {}
    };
    window.addEventListener('beforeunload', onUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', onUnload);
      sendPing('left');
    };
  }, [session, token, bookingId, callEnded]);

  // Load Jitsi External API script — only if LiveKit not configured
  useEffect(() => {
    if (!session || jitsiLoaded) return;
    if (session.livekit_token && session.livekit_url && LiveKitRoom) return; // LiveKit available, skip Jitsi
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    script.onload = () => setJitsiLoaded(true);
    document.head.appendChild(script);
    return () => { if (document.head.contains(script)) document.head.removeChild(script); };
  }, [session, jitsiLoaded]);

  // Auto-start recording
  const startRecording = useCallback(async () => {
    if (mediaRecorderRef.current || hasAutoStarted.current) return;
    hasAutoStarted.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm',
      });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      console.error('Auto-recording failed:', err);
      toast.error('Micro inaccessible');
    }
  }, []);

  // Upload recording
  const uploadRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) return;
    setUploadStatus('uploading');
    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        mediaRecorderRef.current = null;
        if (blob.size > 1000) {
          try {
            const res = await fetch(`${API}/marketplace/sessions/${bookingId}/recording`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'audio/webm' },
              body: blob,
            });
            setUploadStatus(res.ok ? 'done' : 'error');
          } catch { setUploadStatus('error'); }
        } else { setUploadStatus('done'); }
        audioChunksRef.current = [];
        resolve();
      };
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
      if (sessionTimerRef.current) { clearInterval(sessionTimerRef.current); sessionTimerRef.current = null; }
    });
  }, [bookingId, token]);

  // Detect if LiveKit is available — use cached refs for stability
  const useLiveKit = Boolean(lkTokenRef.current && lkUrlRef.current && LiveKitRoom) || Boolean(session?.livekit_token && session?.livekit_url && LiveKitRoom);

  // Auto-hide security banner after 10s
  useEffect(() => {
    if (securityBanner) {
      const timer = setTimeout(() => setSecurityBanner(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [securityBanner]);

  // Initialize Jitsi — FALLBACK when LiveKit not configured
  useEffect(() => {
    if (useLiveKit) return; // LiveKit active, skip Jitsi
    if (!jitsiLoaded || !session || !jitsiContainerRef.current || jitsiApiRef.current) return;

    const displayName = session.user_role === 'coach'
      ? session.coach_display_name || 'Coach'
      : session.client_name || user?.name || 'Participant';

    const jApi = new window.JitsiMeetExternalAPI('meet.jit.si', {
      roomName: session.jitsi_room_id,
      parentNode: jitsiContainerRef.current,
      width: '100%',
      height: '100%',
      configOverwrite: {
        startWithAudioMuted: false, startWithVideoMuted: false,
        prejoinConfig: { enabled: false }, prejoinPageEnabled: false,
        enableLobbyChat: false, hideLobbyButton: true, autoKnockLobby: true,
        toolbarButtons: ['microphone', 'camera', 'desktop', 'chat', 'raisehand', 'tileview', 'fullscreen', 'hangup'],
        toolbarAlwaysVisible: true, disableDeepLinking: true, hideConferenceSubject: true,
        disableInviteFunctions: true, enableClosePage: false, disableThirdPartyRequests: true,
        enableUserRolesBasedOnToken: false, enableFeaturesBasedOnToken: false,
        notifications: [], disableModeratorIndicator: true, requireDisplayName: false, lobbyModeEnabled: false,
      },
      interfaceConfigOverwrite: {
        SHOW_JITSI_WATERMARK: false, SHOW_WATERMARK_FOR_GUESTS: false, SHOW_BRAND_WATERMARK: false,
        SHOW_POWERED_BY: false, SHOW_PROMOTIONAL_CLOSE_PAGE: false, HIDE_INVITE_MORE_HEADER: true,
        TOOLBAR_ALWAYS_VISIBLE: true, DEFAULT_BACKGROUND: '#0A0A0B', MOBILE_APP_PROMO: false,
        BRAND_WATERMARK_LINK: '', JITSI_WATERMARK_LINK: '', PROVIDER_NAME: 'CoachDia', APP_NAME: 'CoachDia',
        AUTHENTICATION_ENABLE: false,
      },
      userInfo: { displayName, email: user?.email || '' },
    });

    jApi.addListener('videoConferenceJoined', () => { toast.success('Session connectée'); startRecording(); });
    jApi.addListener('readyToClose', () => handleEndCall());
    jitsiApiRef.current = jApi;
    return () => { if (jitsiApiRef.current) { jitsiApiRef.current.dispose(); jitsiApiRef.current = null; } };
  }, [useLiveKit, jitsiLoaded, session, user, startRecording]);

  const handleEndCall = useCallback(async () => {
    // Stop server-side recording first
    if (egressIdRef.current) {
      try {
        await fetch(`${API}/marketplace/sessions/${bookingId}/stop-recording`, {
          method: 'POST', headers,
          body: JSON.stringify({ egress_id: egressIdRef.current }),
        });
      } catch {}
    }
    // Stop client-side recording as backup
    if (mediaRecorderRef.current) await uploadRecording();
    if (jitsiApiRef.current) { jitsiApiRef.current.dispose(); jitsiApiRef.current = null; }
    setCallEnded(true);
  }, [uploadRecording, bookingId, headers]);

  // Launch AI analysis — progressive loader
  const launchAnalysis = async () => {
    setAnalysisStatus('transcribing');
    try {
      // Simulate progressive steps for UX (actual call is single)
      const stepTimer = setTimeout(() => setAnalysisStatus('analyzing'), 3000);
      const stepTimer2 = setTimeout(() => setAnalysisStatus('generating'), 8000);

      const res = await fetch(`${API}/marketplace/sessions/${bookingId}/analyze`, {
        method: 'POST', headers,
      });

      clearTimeout(stepTimer);
      clearTimeout(stepTimer2);

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Analyse impossible');
      }
      const data = await res.json();
      setReport(data.report || data);
      setAnalysisStatus('done');
      setShowReport(true);
      toast.success('Rapport IA généré');
    } catch (err) {
      setAnalysisStatus('error');
      toast.error(err.message);
    }
  };

  // Fetch existing report
  const fetchReport = async () => {
    try {
      const res = await fetch(`${API}/marketplace/sessions/${bookingId}/report`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (data.report) { setReport(data.report); setAnalysisStatus('done'); setShowReport(true); }
      }
    } catch {}
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
  const backRoute = session?.user_role === 'coach' ? '/coach/dashboard' : '/my-bookings';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!session) return null;

  // 🔒 Iter382 — Garde d'accès visio (côté frontend, miroir de la garde backend).
  // Le backend ne génère AUCUN token LiveKit si la session est passée/annulée.
  // On affiche ici un écran "Session terminée" propre, sans tentative de connexion.
  if (session.video_access_blocked) {
    const isCoach = session.user_role === 'coach' || session.is_coach_user;
    const reasonLabel = session.video_access_reason === 'session_cancelled'
      ? 'Cette séance a été annulée.'
      : 'Cette séance est terminée — la salle visio n\'est plus accessible.';
    const reviewPath = `/session/${bookingId}/review`;
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4" data-testid="video-access-blocked">
        <div className="max-w-md w-full bg-[#141415] border border-white/10 rounded-2xl p-7 text-center">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="text-white text-lg font-semibold mb-1.5">Session terminée</h1>
          <p className="text-zinc-400 text-sm leading-relaxed mb-6">{reasonLabel}</p>
          <p className="text-zinc-500 text-xs mb-6">
            {formatDateFR(session.date)} · {formatTimeFR(session.start_time)}
            {session.coach_display_name && ` · ${session.coach_display_name}`}
          </p>
          <div className="space-y-2.5">
            {!isCoach && session.video_access_reason !== 'session_cancelled' && (
              <button
                data-testid="review-session-btn-blocked"
                onClick={() => navigate(reviewPath)}
                className="w-full py-3 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#0A0A0B] rounded-xl font-semibold text-sm hover:brightness-110 transition-all"
              >
                Voir / noter la séance
              </button>
            )}
            <button
              data-testid="back-to-bookings-blocked"
              onClick={() => navigate('/my-bookings')}
              className="w-full py-3 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-sm hover:bg-white/10 transition-all"
            >
              Retour à mes sessions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // === POST-SESSION SCREEN ===
  if (callEnded) {
    const isCoach = session.user_role === 'coach' || session.is_coach_user;
    const reviewPath = `/session/${bookingId}/review`;
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="max-w-lg w-full">
          {/* Navigation — TOP, always visible */}
          <div className="mb-6 space-y-3">
            {!isCoach && (
              <button
                data-testid="review-session-btn"
                onClick={() => navigate(reviewPath)}
                className="w-full py-3.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1A0F3A] rounded-xl font-bold text-sm shadow-lg shadow-[#D4AF37]/20 hover:shadow-xl active:scale-[0.99] transition-all flex items-center justify-center gap-2"
              >
                ⭐ Donner mon avis sur la séance
              </button>
            )}
            <button data-testid="go-to-bookings-after-session"
              onClick={() => navigate(backRoute)}
              className="w-full py-3.5 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-sm hover:bg-white/10 transition-all flex items-center justify-center gap-2">
              {isCoach ? 'Retour au dashboard' : 'Mes sessions'} <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Status */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-5">
              {uploadStatus === 'uploading' ? (
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
              ) : (
                <CheckCircle className="w-8 h-8 text-green-400" />
              )}
            </div>
            <h2 className="text-2xl font-light text-white mb-2">Session terminée</h2>
            <p className="text-zinc-400 text-sm">
              {uploadStatus === 'uploading' ? 'Sauvegarde en cours...' :
               uploadStatus === 'done' ? 'Enregistrement sauvegardé.' :
               uploadStatus === 'error' ? 'Erreur de sauvegarde.' : 'Merci pour cette session.'}
            </p>
            {recordingTime > 0 && (
              <p className="text-zinc-500 text-xs mt-2">Durée : {formatTime(recordingTime)}</p>
            )}
          </div>

          {/* AI Analysis Section — COACH (session role OR account role) */}
          {isCoach && (
            <div className="bg-[#141415] border border-white/10 rounded-2xl p-5 mb-6" data-testid="ai-section-coach">
              <h3 className="text-white text-sm font-medium flex items-center gap-2 mb-4">
                <Brain className="w-4 h-4 text-[#D4AF37]" /> Analyse IA de la session
              </h3>

              {analysisStatus === null && (
                <button data-testid="launch-ai-analysis"
                  onClick={launchAnalysis}
                  disabled={uploadStatus === 'uploading'}
                  className="w-full py-3.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#0A0A0B] rounded-xl font-medium text-sm flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-50">
                  <Brain className="w-4 h-4" /> Lancer l'analyse IA
                </button>
              )}

              {['transcribing', 'analyzing', 'generating'].includes(analysisStatus) && (
                <div className="py-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                    <span className="text-zinc-300 text-sm">
                      {analysisStatus === 'transcribing' ? 'Transcription audio...' :
                       analysisStatus === 'analyzing' ? 'Analyse IA en cours...' :
                       'Génération du rapport...'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {['transcribing', 'analyzing', 'generating'].map((step, i) => (
                      <div key={step}
                        className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                          ['transcribing', 'analyzing', 'generating'].indexOf(analysisStatus) >= i
                            ? 'bg-[#D4AF37]' : 'bg-white/10'
                        }`} />
                    ))}
                  </div>
                </div>
              )}

              {analysisStatus === 'done' && !showReport && (
                <button data-testid="show-report-btn"
                  onClick={() => setShowReport(true)}
                  className="w-full py-3 bg-white/5 border border-white/10 text-white rounded-xl text-sm flex items-center justify-center gap-2 hover:bg-white/10 transition-all">
                  <FileText className="w-4 h-4 text-[#D4AF37]" /> Voir le rapport
                </button>
              )}

              {analysisStatus === 'error' && (
                <div className="text-center py-3">
                  <p className="text-red-400 text-sm mb-3">L'analyse n'a pas pu aboutir.</p>
                  <button onClick={launchAnalysis}
                    className="text-[#D4AF37] text-sm font-medium hover:underline">Réessayer</button>
                </div>
              )}

              {analysisStatus === null && (
                <button onClick={fetchReport}
                  className="w-full mt-3 text-zinc-500 text-xs hover:text-zinc-300 transition-colors text-center">
                  Un rapport existe déjà ? Charger
                </button>
              )}
            </div>
          )}

          {/* User post-session — simple message */}
          {!isCoach && (
            <div className="bg-[#141415] border border-white/10 rounded-2xl p-5 mb-6 text-center" data-testid="user-post-session">
              <p className="text-zinc-400 text-sm">Votre coach analysera cette session.</p>
              <p className="text-zinc-500 text-xs mt-2">Le rapport sera disponible dans vos sessions.</p>
            </div>
          )}

          {/* Report display */}
          {showReport && report && (
            <div className="mb-6 max-h-[50vh] overflow-y-auto" data-testid="ai-report-display">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white text-sm font-medium">Rapport complet</span>
                <button onClick={() => setShowReport(false)} className="text-zinc-500 hover:text-white text-xs">
                  Réduire <ChevronDown className="w-3 h-3 inline" />
                </button>
              </div>
              <ReportDisplay report={report} role={isCoach ? 'coach' : 'user'} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // === IN-SESSION SCREEN ===
  return (
    <div className="fixed inset-0 bg-[#0A0A0B] flex flex-col" style={{ height: '100dvh' }}>
      {/* Security banner */}
      {securityBanner && (
        <div className="absolute top-14 left-0 right-0 z-50 px-3 animate-fade-in" data-testid="security-banner">
          <div className="max-w-lg mx-auto bg-[#1a1400] border border-[#D4AF37]/30 rounded-xl p-3 flex items-start gap-3 shadow-lg">
            <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-xs font-medium mb-0.5">Règles de la plateforme</p>
              <p className="text-zinc-400 text-[11px] leading-relaxed">
                Pour des raisons de sécurité, le partage de données personnelles (email, téléphone, liens) est strictement interdit.
              </p>
            </div>
            <button onClick={() => setSecurityBanner(false)} className="text-zinc-500 hover:text-white text-xs flex-shrink-0">✕</button>
          </div>
        </div>
      )}

      {/* PII alert */}
      {piiAlert && (
        <div className="absolute top-14 left-0 right-0 z-50 px-3 animate-fade-in" data-testid="pii-alert">
          <div className="max-w-lg mx-auto bg-red-900/90 border border-red-500/50 rounded-xl p-3 flex items-start gap-3 shadow-lg">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-200 text-xs leading-relaxed">{PII_WARNING}</p>
          </div>
        </div>
      )}

      {/* 🔒 PII Audio Live Toast (Web Speech API — zéro coût) */}
      <PiiWarningToast detection={piiDetection} onDismiss={() => setPiiDetection(null)} />

      {/* Bandeau navigateur non supporté */}
      {piiActive && !piiSupported && (
        <div
          className="absolute top-14 left-0 right-0 z-40 px-3 animate-fade-in"
          data-testid="pii-unsupported-banner"
        >
          <div className="max-w-lg mx-auto bg-zinc-900/90 border border-zinc-700 rounded-xl p-2.5 flex items-center gap-2 shadow-lg">
            <Shield className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <p className="text-zinc-400 text-[11px] leading-snug">
              Protection live indisponible sur votre navigateur — la protection serveur reste active.
            </p>
          </div>
        </div>
      )}

      {/* TOP BAR — fixed height */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#141415] border-b border-white/5 flex-shrink-0 z-30" style={{ height: 52 }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0">
            <h2 className="text-white text-xs font-medium flex items-center gap-1.5 truncate">
              <Video className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
              <span className="truncate">{session.user_role === 'client' ? session.coach_display_name : session.client_name}</span>
            </h2>
            <p className="text-zinc-500 text-[10px]">{formatDateFR(session.date)} | {formatTimeFR(session.start_time)}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(session.user_role === 'coach' || session.is_coach_user) && !['done'].includes(analysisStatus) && (
            <button data-testid="in-session-ai-btn" onClick={launchAnalysis}
              disabled={['transcribing', 'analyzing', 'generating'].includes(analysisStatus)}
              className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#0A0A0B] rounded-full text-[11px] sm:text-xs font-bold disabled:opacity-70 shadow-md hover:shadow-lg transition-shadow whitespace-nowrap">
              {['transcribing', 'analyzing', 'generating'].includes(analysisStatus) ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />
                  <span className="hidden xs:inline">Analyse en cours…</span>
                  <span className="xs:hidden">Analyse…</span>
                </>
              ) : (
                <>
                  <Brain className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="hidden sm:inline">G&#233;n&#233;rer le rapport de s&#233;ance</span>
                  <span className="sm:hidden">G&#233;n&#233;rer le rapport</span>
                </>
              )}
            </button>
          )}
          {(session.user_role === 'coach' || session.is_coach_user) && analysisStatus === 'done' && (
            <button onClick={() => setShowReport(!showReport)}
              data-testid="in-session-view-report-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 rounded-full text-[11px] font-semibold hover:bg-green-500/20 transition-colors whitespace-nowrap">
              <FileText className="w-3 h-3" />
              <span className="hidden sm:inline">Voir le rapport</span>
              <span className="sm:hidden">Rapport</span>
            </button>
          )}
          {isRecording && (
            <div className="flex items-center gap-1 px-2 py-1 bg-red-500/10 border border-red-500/20 rounded-full" data-testid="recording-indicator">
              <Circle className="w-1.5 h-1.5 text-red-500 fill-red-500 animate-pulse" />
              <span className="text-red-400 text-[10px] font-mono">{formatTime(recordingTime)}</span>
            </div>
          )}
          {piiDetection?.rappel_level >= 2 && (
            <div
              className="flex items-center gap-1 px-2 py-1 bg-red-600/20 border border-red-500/40 rounded-full"
              data-testid="pii-persistent-badge"
              title="Rappel confidentialité actif"
            >
              <ShieldAlert className="w-2.5 h-2.5 text-red-400" />
              <span className="text-red-300 text-[10px] font-semibold">Rappel</span>
            </div>
          )}
          {piiSupported && piiListening && (
            <div
              className="flex items-center gap-1 px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full"
              data-testid="pii-active-indicator"
              title="Protection PII live active"
            >
              <Shield className="w-2.5 h-2.5 text-emerald-400" />
            </div>
          )}
          {!isRecording && sessionElapsed > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded-full" data-testid="session-timer">
              <Clock className="w-2.5 h-2.5 text-zinc-400" />
              <span className="text-zinc-400 text-[10px] font-mono">{formatTime(sessionElapsed)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Report overlay */}
      {showReport && report && (
        <div className="absolute inset-0 z-40 bg-[#0A0A0B]/95 backdrop-blur overflow-y-auto pt-14 pb-6 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[#D4AF37] text-lg font-medium flex items-center gap-2">
                <FileText className="w-5 h-5" /> Rapport
              </h2>
              <button onClick={() => setShowReport(false)} className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20">Fermer</button>
            </div>
            <ReportDisplay report={report} role={session.user_role} />
          </div>
        </div>
      )}

      {/* VIDEO + CONTROLS — LiveKit (hooks inside context) or Jitsi fallback */}
      {useLiveKit ? (
        <LiveKitRoom
          serverUrl={lkUrlRef.current || session.livekit_url}
          token={lkTokenRef.current || session.livekit_token}
          connect={true}
          video={true}
          audio={true}
          onConnected={() => {
            toast.success('Session connectée');
            // Start session elapsed timer
            if (!sessionTimerRef.current) {
              sessionTimerRef.current = setInterval(() => setSessionElapsed(t => t + 1), 1000);
            }
            // Delay Egress start — let room stabilize before server-side recording
            setTimeout(() => startEgress(), 2000);
          }}
          onDisconnected={(reason) => {
            if (callEnded) return; // Already on post-session screen
            // Only end for permanent disconnects — let LiveKit auto-reconnect for transient issues
            const permanent = [
              LkDisconnectReason?.CLIENT_INITIATED,
              LkDisconnectReason?.PARTICIPANT_REMOVED,
              LkDisconnectReason?.ROOM_DELETED,
              LkDisconnectReason?.ROOM_CLOSED,
              LkDisconnectReason?.DUPLICATE_IDENTITY,
            ].filter(Boolean);
            if (permanent.includes(reason)) {
              handleEndCall();
            }
            // SIGNAL_CLOSE, STATE_MISMATCH, MIGRATION, UNKNOWN → LiveKit reconnects automatically
          }}
          data-lk-theme="default"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <LiveKitInner
            chatOpen={chatOpen}
            setChatOpen={setChatOpen}
            onEndCall={handleEndCall}
            setPiiAlert={setPiiAlert}
          />
        </LiveKitRoom>
      ) : (
        <>
          <div ref={jitsiContainerRef} data-testid="jitsi-container"
            className="flex-1 bg-[#0A0A0B]" style={{ minHeight: 0 }} />
          <div className="flex-shrink-0 bg-[#141415] border-t border-white/5 flex items-center justify-center px-4 z-30"
            data-testid="fixed-control-bar"
            style={{ height: 64, paddingBottom: 'max(4px, env(safe-area-inset-bottom))' }}>
            <button data-testid="btn-hangup" onClick={() => { if (window.confirm('Quitter la session ?')) handleEndCall(); }}
              className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-400 hover:bg-red-500/30 transition-all">
              <PhoneOff className="w-5 h-5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
